import { css } from "@emotion/react";
import { ChevronLeftIcon, ChevronRightIcon, SquareXIcon } from "lucide-react";
import {
	type ProductCategoriesTree,
	useInventoryState,
} from "../../../hooks/useInventoryState";
import { useStorefrontSettingsState } from "../../../hooks/useStorefrontSettingsState";
import { useTailwindMediaQueries } from "../../../hooks/useTailwindMediaQueries";
import { useThemeState } from "../../../hooks/useThemeState";
import { useTranslate } from "../../../hooks/useTranslate/useTranslate";
import { CatalogMenu } from "../../CatalogMenu";
import { ReservationDates } from "../../CheckoutForm/ReservationDates";
import { SelectLocation } from "../../CheckoutForm/SelectLocation";
import { CustomScrollbars } from "../../CustomScrollbars/CustomScrollbars";
import { Button } from "../../ui/button";
import { CategoryTreeControl } from "./CategoryTree";

//-------------------------------------------------------------------------------------------------------
//export type SidebarProps = {};

export const Sidebar: React.FC /*<SidebarProps>*/ = (_props) => {
	const themeState = useThemeState();
	const theme = themeState.get().theme;
	const inventoryState = useInventoryState();
	const sidebarExpanded = inventoryState.get().sidebarExpanded;
	const sidebarCollapsedWidth = 40;
	const settingsState = useStorefrontSettingsState();
	const isUniversity = settingsState.get().settings?.IsUniversity ?? false;
	const mediaQueries = useTailwindMediaQueries();
	const translate = useTranslate();
	return (
		<div
			css={css`flex:1 1 0;display:flex;font-size:${mediaQueries.desktop ? "1rem" : "1rem"};`}
		>
			<div
				css={css`
                    flex: 1 1 0;
                    display:  ${sidebarExpanded ? "flex" : "none"};
                    flex-direction:column;
                    overflow:hidden;
                `}
				style={{
					marginLeft: sidebarExpanded ? "0" : `${sidebarCollapsedWidth}px`,
				}}
			>
				{!isUniversity && (
					<div
						css={css`
							flex:0;
							padding:.5em .5em 0 .5em;
							display:flex;
						`}
					>
						<SelectLocation disabled={false} />
					</div>
				)}

				{(inventoryState.get().catalog?.HasRental ?? false) &&
					(mediaQueries.phone || mediaQueries.desktop) && (
						<div
							css={css`
								flex:0 0 auto;
								padding:0 .5em;
								.field {
									margin:1em 0 2px 0;
								}
								.caption {
									text-align:center;
									color: ${theme.palette.siteSideBarText};
								}
								.flex {
									display:flex;
								}
                            `}
						>
							<div className="flex">
								<div css={css`display:flex;flex-wrap:wrap;`}>
									<div css={css`padding-top:12px;`}>
										<ReservationDates
											fullwidth={true}
											catalogId={inventoryState.get().catalog?.Id}
										/>
									</div>
									{/* <div css={css`padding-top:12px;`}>
                                    <CheckoutDate type="stopdate" fullwidth={true} catalogId={inventoryState.get().catalog?.Id} />
                                </div> */}
								</div>
								{/* <div css={css`display:flex;justify-content:flex-end;align-items:center;`}>
                                <Tooltip title={translate('Reservations for different date ranges must be submitted as separate Orders')} arrow>
                                    <Help />
                                </Tooltip>
                            </div> */}
							</div>
						</div>
					)}
				<div
					css={css`
                        flex:0;
                        font-size:1rem;
                        line-height:2rem;
                        font-weight:700;
                        color: ${theme.palette.siteSideBarText};
                        padding:.5rem 0 .1rem 0;
                        display:flex;
                        align-items: center;
                        justify-content: flex-start;
                        font-size:1.1em;`}
				>
					{/* <ImportContacts css={css`padding:0 4px;`} /> */}
					<CatalogMenu font-size="small" />
				</div>
				<div
					css={css`
                        flex:1 1 0;
                        padding: 0 1em;
                        display:flex;
                        flex-direction:column;
                    `}
				>
					<CustomScrollbars
						css={css`
                            flex: 1 1 0;
                            overflow-x:hidden;
                        `}
					>
						{inventoryState.get().catalog && (
							<CategoryTreeControl
								catalog={inventoryState.get().catalog}
								rentalCategoriesTree={
									inventoryState.get()
										.productCategoriesTree as ProductCategoriesTree
								}
							/>
						)}
					</CustomScrollbars>
				</div>
			</div>

			{/* Expand/Collapse button */}
			{mediaQueries.minw_md && (
				<div className="flex-none flex justify-end mt-2">
					<Button
						title={
							sidebarExpanded
								? translate("Collapse Sidebar")
								: translate("Expand Sidebar")
						}
						variant="outline"
						className="mx-1 size-10 xl:size-8"
						onClick={async () => {
							return Promise.resolve().then(() => {
								inventoryState.setSidebarExpanded(!sidebarExpanded);
							});
						}}
					>
						{sidebarExpanded && (
							<ChevronLeftIcon className="mx-1 size-5.5 xl:size-4" />
						)}
						{!sidebarExpanded && (
							<ChevronRightIcon className="mx-1 size-5.5 xl:size-4" />
						)}
					</Button>
				</div>
			)}
			{!mediaQueries.minw_md && sidebarExpanded && (
				<div
					css={css`flex:0 0 auto;display:flex;flex:0 0 auto;justify-content:flex-end;`}
				>
					<Button
						variant="ghost"
						onClick={async () => {
							return Promise.resolve().then(() => {
								inventoryState.setSidebarExpanded(!sidebarExpanded);
							});
						}}
					>
						<SquareXIcon
							css={css`color:oklch(0.65 0.3 29.34);width:2rem;height:2rem;box-shadow: 0 0px 32px rgba(255, 0, 0, 0.3);`}
						/>
					</Button>
				</div>
			)}
		</div>
	);
};
